import { lazy, memo, Suspense, useCallback, useEffect, useMemo } from 'react';
import { IComplianceGroup, IEvaluatedJurisdictionView } from '@/@types/client-api';
import Modal, { ModalProps } from '@/components/ui/Modal/Modal';
import ComplianceForm, {
  ComplianceFormFields
} from '@/components/ui/CreditTracker/Forms/ComplianceForm';
import useComplianceModal from '@/components/ui/CreditTracker/hooks/useComplianceModalHook';
import Icon from '@/components/ui/Icon';
import ExperienceLevelSelector from '@/components/ui/CreditTracker/snippets/ExperienceLevelSelector';
import { CompliancePeriodValue, SpecialStatusSelector } from '../util/constants';

const ComplianceModalStepContent = lazy(
  () => import('@/components/ui/CreditTracker/Modals/ComplianceModalStepContent')
);

export interface ComplianceModalProps extends ModalProps {
  customerSk: number;
  isMissingCompliancePeriod: boolean;
  eyebrowText?: string;
  jurisdiction?: IEvaluatedJurisdictionView;
  complianceFormFields?: ComplianceFormFields;
  complianceGroups?: IComplianceGroup[] | null;
  selectedPeriod?: CompliancePeriodValue | null;
  specialStatuses?: SpecialStatusSelector[];
  isTransitionalOrNewlyAdmittedRegion?: boolean;
  onComplete?: () => void;
}

const ComplianceModal = memo((props: ComplianceModalProps): JSX.Element => {
  const {
    open,
    setOpen,
    eyebrowText,
    complianceFormFields,
    complianceGroups,
    selectedPeriod,
    specialStatuses,
    isTransitionalOrNewlyAdmittedRegion,
    onComplete,
    customerSk,
    isMissingCompliancePeriod
  } = props;

  const {
    compliancePeriod,
    handleChange,
    handleResetOnClose,
    handleResetComplianceDates,
    handleSaveButton,
    reportingPeriod,
    fieldsWithErrors,
    setFieldsWithErrors,
    isSubmitting,
    isUpdatingDateRange,
    isUpdatingForm,
    showReset,
    selectedRegion,
    stepIndex,
    setStepIndex,
    specialStatus: {
      handleSelectorChange,
      handleSpecialStatusValidation,
      setStatuses,
      showSpecialStatusError,
      statuses
    }
  } = useComplianceModal({
    customerSk,
    complianceFormFields,
    setOpen,
    open
  });

  const formContent = useMemo(
    () => (
      <ComplianceForm
        jurisdiction={selectedRegion?.jurisdiction}
        currentCompliancePeriod={compliancePeriod}
        complianceGroups={complianceGroups ?? []}
        onChange={handleChange}
        onResetDates={handleResetComplianceDates}
        reportingPeriod={reportingPeriod}
        fieldsWithErrors={fieldsWithErrors}
        setFieldsWithErrors={setFieldsWithErrors}
        isUpdatingDateInterval={isUpdatingDateRange}
        isUpdatingForm={isUpdatingForm}
        showReset={showReset}
        selectedPeriod={selectedPeriod}
      />
    ),
    [
      complianceGroups,
      compliancePeriod,
      fieldsWithErrors,
      handleChange,
      handleResetComplianceDates,
      isUpdatingDateRange,
      isUpdatingForm,
      reportingPeriod,
      selectedPeriod,
      selectedRegion?.jurisdiction,
      setFieldsWithErrors,
      showReset
    ]
  );

  const specialStatusContent = useMemo(
    () => (
      <div>
        {isTransitionalOrNewlyAdmittedRegion && (
          <div className="mb-4">{`Are you newly-admitted or experienced? *`}</div>
        )}
        {specialStatuses && (
          <ExperienceLevelSelector
            statuses={statuses}
            onChange={handleSelectorChange}
            selectorSize="large"
            showError={showSpecialStatusError}
          />
        )}
      </div>
    ),
    [
      handleSelectorChange,
      isTransitionalOrNewlyAdmittedRegion,
      showSpecialStatusError,
      specialStatuses,
      statuses
    ]
  );

  const handleIncrementStep = useCallback(() => {
    setStepIndex(prev => prev + 1);
  }, [setStepIndex]);

  const handleFormSave = useCallback(async () => {
    const res = await handleSaveButton();
    if (res && onComplete) {
      onComplete();
    }
  }, [handleSaveButton, onComplete]);

  const steps = useMemo(() => {
    const steps: JSX.Element[] = [];
    const stepNumberText = specialStatuses && (
      <div className="text-2 mb-2 font-normal text-black">{`${isMissingCompliancePeriod ? stepIndex : stepIndex + 1} of 2`}</div>
    );

    if (isMissingCompliancePeriod) {
      /** Setup warning modal */
      steps.push(
        <ComplianceModalStepContent
          handleSaveButtonPress={handleIncrementStep}
          saveButtonLabel="Continue"
          content={MissingComplianceContent}
          isMultiStep
          isSubmitting={isSubmitting || false}
        />
      );

      if (specialStatuses?.length) {
        /** Setup special status selector step */
        steps.push(
          <ComplianceModalStepContent
            eyebrow={{
              size: 'large',
              text: (
                <div>
                  {stepNumberText}
                  {eyebrowText}
                </div>
              )
            }}
            title="Confirm your details"
            subtitle="* Indicates required field"
            handleSaveButtonPress={handleSpecialStatusValidation}
            saveButtonLabel="Continue"
            content={specialStatusContent}
            isMultiStep
            isSubmitting={!!isSubmitting || !!isUpdatingForm}
          />
        );
      }
    }

    /** Compliance form step */
    steps.push(
      <ComplianceModalStepContent
        eyebrow={
          steps?.length
            ? {
                size: 'large',
                text: (
                  <div>
                    {isMissingCompliancePeriod && stepNumberText}
                    {eyebrowText}
                  </div>
                )
              }
            : undefined
        }
        title={isMissingCompliancePeriod ? `Add compliance information` : `Edit compliance period`}
        subtitle="* Indicates required field"
        saveButtonLabel="Save"
        handleSaveButtonPress={handleFormSave}
        content={formContent}
        isSubmitting={isSubmitting || false}
        handleCloseButton={handleResetOnClose}
        isMultiStep
      />
    );

    return steps;
  }, [
    eyebrowText,
    formContent,
    handleResetOnClose,
    handleFormSave,
    handleSpecialStatusValidation,
    handleIncrementStep,
    isMissingCompliancePeriod,
    isSubmitting,
    isUpdatingForm,
    specialStatusContent,
    specialStatuses,
    stepIndex
  ]);

  useEffect(() => {
    if (open) {
      setStepIndex(0);
      if (specialStatuses) {
        setStatuses(specialStatuses);
      }
    } else {
      setFieldsWithErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const currentStep = useMemo(() => steps[stepIndex], [stepIndex, steps]);

  return (
    <Modal open={open} setOpen={setOpen}>
      <Suspense fallback={null}>{currentStep}</Suspense>
    </Modal>
  );
});

const MissingComplianceContent = (
  <div className="space-y-8">
    <div className="mx-auto flex size-[300px] items-center justify-center rounded-full bg-gray-lightest">
      <div className="flex size-[120px] items-center justify-center rounded-full bg-gray-darker">
        <Icon name="upcoming" size="jumbo" className="text-white" />
      </div>
    </div>
    <div className="space-y-4">
      <h4 className="heading-4">We can't calculate credits without your compliance period.</h4>
      <p>Your credit tracker setup is almost done; we just need some missing details from you.</p>
    </div>
  </div>
);

ComplianceModal.displayName = 'ComplianceModal';
export default ComplianceModal;
